import React, { useEffect, useState } from 'react';
import { fetchTrips } from '../api';
import Dropdown, { DropdownOption } from '../../../components/ui/Dropdown';

// Single reusable trip-selection control, used everywhere a trip is picked
// (Journey Planner "Add to trip", Unsorted "Assign to a trip…"). It owns the
// LOOK and the option list; each caller owns the behavior via onSelect.
//
// Two modes, same component:
//  - Controlled (pass `value`): a persistent selection (planner). First option
//    is a real, selectable "No trip (Unsorted)".
//  - Action (omit `value`): always shows the placeholder and snaps back after a
//    choice (unsorted row fires an assignment immediately).

export interface TripOption {
  id: string;
  title: string;
}

export type TripChoice = { tripId: string | null } | { newTripTitle: string };

const NEW = '__new__';

export interface TripPickerProps {
  /** Provide options, or omit to have the picker fetch the user's trips. */
  trips?: TripOption[];
  /** Controlled selected trip id (null = "No trip"). Omit for action mode. */
  value?: string | null;
  /** Override the first option's label. */
  firstOptionLabel?: string;
  /** Offer "Create new trip…" (prompts for a title). */
  includeCreateNew?: boolean;
  /** Pre-fill the "Create new trip" prompt (e.g. the journey's name). Editable. */
  newTripDefaultName?: string;
  onSelect: (choice: TripChoice) => void;
  label?: string;
  disabled?: boolean;
}

const TripPicker: React.FC<TripPickerProps> = ({
  trips,
  value,
  firstOptionLabel,
  includeCreateNew,
  newTripDefaultName,
  onSelect,
  label,
  disabled,
}) => {
  const controlled = value !== undefined;
  const selfFetch = trips === undefined;
  const [fetched, setFetched] = useState<TripOption[]>([]);
  const [loaded, setLoaded] = useState(!selfFetch);

  useEffect(() => {
    if (!selfFetch) return;
    let active = true;
    (async () => {
      try {
        const { trips: list } = await fetchTrips();
        if (active) setFetched(list.map((t) => ({ id: t.id, title: t.title })));
      } catch (_) {
        // Non-critical; the first option is still available.
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [selfFetch]);

  const options = trips ?? fetched;

  // Controlled: reset a value pointing at a trip the user doesn't have (e.g. a
  // stale/deleted ?tripId=), so we never act on a ghost trip.
  useEffect(() => {
    if (controlled && value && loaded && !options.some((t) => t.id === value)) {
      onSelect({ tripId: null });
    }
  }, [controlled, value, loaded, options, onSelect]);

  // Action mode always shows the placeholder/first option (value stays ''),
  // snapping back after a choice; controlled mode reflects the selection.
  const handlePick = (v: string) => {
    if (v === NEW) {
      const title = window.prompt('Name the new trip:', newTripDefaultName ?? '');
      if (title && title.trim()) onSelect({ newTripTitle: title.trim() });
      return;
    }
    if (v === '') {
      if (controlled) onSelect({ tripId: null });
      return; // action mode: selecting the placeholder is a no-op
    }
    onSelect({ tripId: v });
  };

  const dropdownOptions: DropdownOption[] = [
    { value: '', label: firstOptionLabel ?? (controlled ? 'No trip (Unsorted)' : 'Assign to a trip…') },
    ...options.map((t) => ({ value: t.id, label: t.title })),
    ...(includeCreateNew ? [{ value: NEW, label: '＋ Create new trip…' }] : []),
  ];

  // Reuse the standard styled Dropdown. stopPropagation keeps clicks from
  // bubbling to a clickable parent row (the Unsorted list cards).
  const control = (
    <div onClick={(e) => e.stopPropagation()} style={{ width: 240, maxWidth: '100%' }}>
      <Dropdown
        ariaLabel={label || 'Select a trip'}
        options={dropdownOptions}
        value={controlled ? value ?? '' : ''}
        onChange={handlePick}
        disabled={disabled}
      />
    </div>
  );

  if (!label) return control;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#16241d' }}>
      <span style={{ fontWeight: 600, flexShrink: 0 }}>{label}:</span>
      {control}
    </div>
  );
};

export default TripPicker;
